import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { BACKEND_URL } from '../config';
import { useOrganization } from '../context/OrganizationContext';
import AgentBillingBadge from './AgentBillingBadge';

import {
  ArrowsClockwise,
  Calendar,
  CheckCircle,
  CircleNotch,
  Clock,
  CreditCard,
  PauseCircle,
  PlayCircle,
  WarningCircle,
  XCircle
} from '@phosphor-icons/react';
const AgentSubscriptionCard = ({ agent, onUpdate }) => {
  const { activeOrganization } = useOrganization();
  const [billingInfo, setBillingInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [canceling, setCanceling] = useState(false);
  const [pausing, setPausing] = useState(false);

  useEffect(() => {
    fetchBillingInfo();
  }, [agent.id]);

  const fetchBillingInfo = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/agents/${agent.id}/billing/status`, {
        headers: {
          'X-Organization-Id': activeOrganization?.organization_id
        },
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setBillingInfo(data);
      }
    } catch (error) {
      console.error('Error fetching billing info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePauseAgent = async () => {
    const isPaused = agent.status === 'paused';
    const action = isPaused ? 'resume' : 'pause';
    
    if (!isPaused && !window.confirm('Are you sure you want to pause this agent? It will stop processing tasks until resumed.')) {
      return;
    }

    setPausing(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/agents/${agent.id}/${action}`, {
        method: 'POST',
        headers: {
          'X-Organization-Id': activeOrganization?.organization_id
        },
        credentials: 'include'
      });
      
      if (response.ok) {
        if (onUpdate) onUpdate();
      } else {
        throw new Error(`Failed to ${action} agent`);
      }
    } catch (error) {
      console.error(`Error ${action}ing agent:`, error);
      alert(`Failed to ${action} agent. Please try again.`);
    } finally {
      setPausing(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!billingInfo?.subscription_id) return;
    
    if (!window.confirm('Are you sure you want to cancel this subscription? The agent will be deactivated at the end of the current billing period.')) {
      return;
    }

    setCanceling(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/agents/${agent.id}/billing/cancel`, {
        method: 'POST',
        headers: {
          'X-Organization-Id': activeOrganization?.organization_id
        },
        credentials: 'include'
      });
      
      if (response.ok) {
        await fetchBillingInfo();
        if (onUpdate) onUpdate();
        alert('Subscription canceled successfully. Agent will remain active until the end of the current billing period.');
      } else {
        throw new Error('Failed to cancel subscription');
      }
    } catch (error) {
      console.error('Error canceling subscription:', error);
      alert('Failed to cancel subscription. Please try again or contact support.');
    } finally {
      setCanceling(false);
    }
  };

  if (loading) {
    return (
      <div className="glass rounded-2xl p-6 flex items-center justify-center">
        <CircleNotch size={32} className="animate-spin text-[#6366F1]" />
      </div>
    );
  }

  // If agent is active but no billing info, show "Manually Activated" status card
  // This happens when an admin enables an agent without going through Stripe
  if (!billingInfo) {
    if (agent.status === 'active') {
      return (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass rounded-2xl p-6"
        >
          <div className="flex items-start justify-between mb-4">
            <h3 className="text-xl font-bold text-white flex items-center gap-2">
              <CreditCard size={24} className="text-[#6366F1]" />
              Billing & Subscription
            </h3>
            <AgentBillingBadge 
              billingStatus={null}
              isActive={true}
            />
          </div>
          <p className="text-[#A0AEC0] text-sm">
            This agent was manually activated by an administrator and is running without a billing subscription.
          </p>
          
          {/* Pause/Resume Button */}
          <div className="mt-6 pt-4 border-t border-[rgba(255,255,255,0.1)]">
            <button
              onClick={handlePauseAgent}
              disabled={pausing}
              className="w-full px-4 py-3 rounded-xl glass hover:bg-amber-500/10 text-amber-400 font-medium flex items-center justify-center gap-2 transition-colors"
            >
              {pausing ? (
                <CircleNotch size={18} className="animate-spin" />
              ) : (
                <>
                  <PauseCircle size={18} />
                  Pause Agent
                </>
              )}
            </button>
          </div>
        </motion.div>
      );
    }
    return null;
  }

  const priceDollars = (billingInfo.price_cents / 100).toFixed(2);
  const isHostingFree = billingInfo.hosting_fee_enabled === false || billingInfo.price_cents === 0;
  const hasActiveSubscription = billingInfo.subscription_id && billingInfo.billing_status === 'active';
  const renewalDate = billingInfo.next_renewal_date ? new Date(billingInfo.next_renewal_date) : null;
  const isPaused = agent.status === 'paused';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass rounded-2xl p-6"
    >
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
            <CreditCard size={24} className="text-[#6366F1]" />
            Billing & Subscription
          </h3>
        </div>
        
        <AgentBillingBadge 
          billingStatus={billingInfo.billing_status}
          isActive={billingInfo.is_active}
        />
      </div>

      {/* Price Card - Full Width */}
      <div className="glass rounded-xl p-5 mb-6">
        <div className="flex items-center gap-2 text-[#A0AEC0] text-sm mb-2">
          {isHostingFree ? 'Hosting' : 'Monthly Cost'}
        </div>
        {isHostingFree ? (
          <>
            <p className="text-[#14F195] font-bold text-2xl">
              Free
            </p>
            <p className="text-[#A0AEC0] text-xs mt-1">AI compute powered by credits</p>
          </>
        ) : (
          <p className="text-white font-bold text-2xl">
            ${priceDollars}
            <span className="text-sm text-[#A0AEC0] font-normal">/month</span>
          </p>
        )}
      </div>

      {/* Next Renewal - if applicable */}
      {renewalDate && (
        <div className="glass rounded-xl p-4 mb-6">
          <div className="flex items-center gap-2 text-[#A0AEC0] text-sm mb-2">
            <Calendar size={16} />
            Next Renewal
          </div>
          <p className="text-white font-bold text-lg">
            {renewalDate.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            })}
          </p>
        </div>
      )}

      {/* Subscription ID */}
      {billingInfo.subscription_id && (
        <div className="glass rounded-xl p-4 mb-6">
          <div className="flex items-center gap-2 text-[#A0AEC0] text-sm mb-2">
            <CheckCircle size={16} />
            Subscription ID
          </div>
          <p className="text-white font-mono text-sm break-all">
            {billingInfo.subscription_id}
          </p>
        </div>
      )}

      {/* Status Messages */}
      {billingInfo.billing_status === 'pending_payment' && (
        <div className="mb-4 p-4 rounded-xl bg-[#F59E0B]/20 border border-[#F59E0B]/30">
          <div className="flex items-start gap-3">
            <Clock size={20} className="text-[#F59E0B] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-[#F59E0B] font-semibold mb-1">Payment Pending</p>
              <p className="text-[#F59E0B] text-sm">
                Complete your payment to activate this agent. You should have been redirected to Stripe checkout.
              </p>
            </div>
          </div>
        </div>
      )}

      {billingInfo.billing_status === 'past_due' && (
        <div className="mb-4 p-4 rounded-xl bg-[#F97316]/20 border border-[#F97316]/30">
          <div className="flex items-start gap-3">
            <WarningCircle size={20} className="text-[#F97316] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-[#F97316] font-semibold mb-1">Payment Failed</p>
              <p className="text-[#F97316] text-sm">
                Your last payment failed. Please update your payment method to avoid service interruption.
              </p>
            </div>
          </div>
        </div>
      )}

      {billingInfo.billing_status === 'canceled' && (
        <div className="mb-4 p-4 rounded-xl bg-[#EF4444]/20 border border-[#EF4444]/30">
          <div className="flex items-start gap-3">
            <XCircle size={20} className="text-[#EF4444] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-[#EF4444] font-semibold mb-1">Subscription Canceled</p>
              <p className="text-[#EF4444] text-sm">
                Your subscription has been canceled. Reactivate to continue using this agent.
              </p>
            </div>
          </div>
        </div>
      )}

      {billingInfo.billing_status === 'trialing' && (
        <div className="mb-4 p-4 rounded-xl bg-[#6366F1]/20 border border-[#6366F1]/30">
          <div className="flex items-start gap-3">
            <CheckCircle size={20} className="text-[#6366F1] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-[#6366F1] font-semibold mb-1">Free Trial Active</p>
              <p className="text-[#6366F1] text-sm">
                You&apos;re currently in a free trial period. Billing begins on {renewalDate?.toLocaleDateString()}.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-col gap-3">
        {/* Pause/Resume Agent Button - Always show for active agents */}
        {(billingInfo.billing_status === 'active' || billingInfo.is_active) && (
          <button
            onClick={handlePauseAgent}
            disabled={pausing}
            className={`w-full px-6 py-3 rounded-full font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2 ${
              isPaused 
                ? 'bg-[#10B981]/20 hover:bg-[#10B981]/30 text-[#10B981]' 
                : 'bg-[#F59E0B]/20 hover:bg-[#F59E0B]/30 text-[#F59E0B]'
            }`}
          >
            {pausing ? (
              <>
                <CircleNotch size={18} className="animate-spin" />
                {isPaused ? 'Resuming...' : 'Pausing...'}
              </>
            ) : isPaused ? (
              <>
                <PlayCircle size={18} />
                Resume Agent
              </>
            ) : (
              <>
                <PauseCircle size={18} />
                Pause Agent
              </>
            )}
          </button>
        )}

        {hasActiveSubscription && (
          <button
            onClick={handleCancelSubscription}
            disabled={canceling}
            className="w-full px-6 py-3 rounded-full bg-[#EF4444]/20 hover:bg-[#EF4444]/30 text-[#EF4444] font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {canceling ? (
              <>
                <CircleNotch size={18} className="animate-spin" />
                Canceling...
              </>
            ) : (
              <>
                <XCircle size={18} />
                Cancel Subscription
              </>
            )}
          </button>
        )}

        {billingInfo.billing_status === 'canceled' && (
          <button
            onClick={() => window.location.reload()}
            className="w-full px-6 py-3 rounded-full bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white font-semibold transition-all flex items-center justify-center gap-2"
          >
            <ArrowsClockwise size={18} />
            Reactivate Agent
          </button>
        )}

        {billingInfo.billing_status === 'past_due' && (
          <button
            onClick={() => {
              alert('Please contact support to update your payment method.');
            }}
            className="w-full px-6 py-3 rounded-full bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white font-semibold transition-all flex items-center justify-center gap-2"
          >
            <CreditCard size={18} />
            Update Payment Method
          </button>
        )}
      </div>

      {/* Help Text */}
      <div className="mt-6 pt-6 border-t border-[rgba(255,255,255,0.1)]">
        <p className="text-[#A0AEC0] text-sm text-center">
          Need help? Contact support or{' '}
          <a href="/pricing" className="text-[#6366F1] hover:underline">
            view pricing details
          </a>
        </p>
      </div>
    </motion.div>
  );
};

export default AgentSubscriptionCard;
